import { effect, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Params, Router } from '@angular/router';
import { filter } from 'rxjs';
import { Organization } from '../../shared/types/organization';
import { OrganizationService } from './organization.service';
import { ApplicationStore, ApplicationStoreType } from '../store/application.store';

const LAST_ORGANIZATION_STORAGE_KEY = 'lastSelectedOrganizationId';

@Injectable({ providedIn: 'root' })
export class OrganizationUrlSyncService {
    private router = inject(Router);
    private organizationService = inject(OrganizationService);
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);

    constructor() {
        // Write side (selection-driven): when the selected org changes while the router is idle
        // (e.g. switched via the sidebar), update ?orgId immediately. If a navigation is in progress
        // (e.g. selection driven by a route guard during a redirect), the NavigationEnd handler below
        // applies it once that navigation settles, so this sync never cancels an in-flight navigation.
        effect(() => {
            const organization = this.applicationStore.selectedOrganization();
            if (!organization) return;

            localStorage.setItem(LAST_ORGANIZATION_STORAGE_KEY, organization.id);
            if (this.router.getCurrentNavigation()) return;

            this.writeOrgId(organization.id);
        });

        // Write side (navigation-driven): after every navigation, re-attach ?orgId if it was dropped
        // (in-app navigations do not preserve query params) and strip any inbound ?chainId. It does
        // NOT overwrite a different orgId already present — that is the read side's cue to switch org.
        this.router.events
            .pipe(
                filter((event) => event instanceof NavigationEnd),
                takeUntilDestroyed(),
            )
            .subscribe(() => {
                const organization = this.applicationStore.selectedOrganization();
                if (!organization) return;

                const params = this.currentQueryParams();
                const orgIdMissing = !params['orgId'];
                const chainIdPresent = params['chainId'] != null;
                if (orgIdMissing || chainIdPresent) {
                    this.writeOrgId(organization.id);
                }
            });

        // Read side: react to URL changes (back/forward, link paste) and re-select.
        this.router.routerState.root.queryParamMap.pipe(takeUntilDestroyed()).subscribe((params) => {
            const orgId = params.get('orgId');
            const organizations = this.applicationStore.organizations();

            if (!orgId || organizations.length === 0) return;
            if (orgId === this.applicationStore.selectedOrganization()?.id) return;

            const match = organizations.find((o) => o.id === orgId);
            if (match) {
                this.organizationService.selectOrganization(match);
            } else {
                // Unknown / lost-access org id — fall back; the write effect rewrites the URL.
                this.resolveSelectionFromUrl(organizations);
            }
        });
    }

    private currentQueryParams(): Params {
        const navigation = this.router.getCurrentNavigation();
        const inFlightUrl = navigation?.finalUrl ?? navigation?.extractedUrl;

        return inFlightUrl?.queryParams ?? this.router.routerState.snapshot.root.queryParams;
    }

    private writeOrgId(orgId: string): void {
        const params = this.currentQueryParams();
        if (params['orgId'] === orgId && params['chainId'] == null) return;

        this.router.navigate([], {
            queryParams: { orgId, chainId: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
        });
    }

    public resolveSelectionFromUrl(organizations: Organization[]): void {
        if (!organizations || organizations.length === 0) return;

        const params = this.currentQueryParams();
        const orgId: string | undefined = params['orgId'];
        const chainId: string | undefined = params['chainId'];

        const byOrgId = orgId ? organizations.find((o) => o.id === orgId) : null;
        const byChainId = !byOrgId && chainId ? organizations.find((o) => o.chainId === Number(chainId)) : null;
        const lastSelectedId = localStorage.getItem(LAST_ORGANIZATION_STORAGE_KEY);
        const byLastSelected = lastSelectedId ? organizations.find((o) => o.id === lastSelectedId) : null;

        const organization = byOrgId ?? byChainId ?? byLastSelected ?? organizations[0];
        this.organizationService.selectOrganization(organization);
    }
}
